// Library Methods
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import TitleWithTooltip from "src/components/shared/text/TitleWithTooltip/TitleWithTooltip";
import CustomCheckbox from "src/components/shared/inputs/CustomCheckbox/CustomCheckbox";

// Types
import { SelectedExamCardType } from "src/services/exams/types";

// Contexts
import { useExamSliderContext } from "src/contexts/exams/ExamSliderContext";

// Styles
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

const MAX_LENS_SELECTION = 3;
const SELECTED_COLOR = "#E2772E";

// The minimal shape SelectLensButton needs from a lens -- both IOLLensType
// and ProgressiveLensType satisfy this structurally, so callers can pass
// either without any adapting.
type LensType = {
  id: string;
  name: string;
  lensType: string;
};

type SelectLensButtonType<T extends LensType> = {
  lensType: T;
  selectedLensArray: SelectedExamCardType["lensIds"];
  updateExamCardProperty: (
    propertyName: keyof SelectedExamCardType,
    value: SelectedExamCardType[keyof SelectedExamCardType]
  ) => void;
  // IOL vs Progressive simulation
  examAlgorithmPath: string;
  // Formatted and translated string with newlines included
  tooltip: string;
};

const SelectLensButton = <T extends LensType>({
  lensType,
  selectedLensArray = [],
  updateExamCardProperty,
  examAlgorithmPath,
  tooltip,
}: SelectLensButtonType<T>) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { inputBorder } = getSurfaceTokens(theme);
  const { actions } = useExamSliderContext();
  const { setSelectedExamCards } = actions;

  const isSelected = Boolean(
    selectedLensArray?.find((id) => id === lensType.id)
  );

  const isDisabledButton =
    selectedLensArray?.length >= MAX_LENS_SELECTION && !isSelected;

  const onRemoveExam = useCallback(() => {
    setSelectedExamCards((prev: Array<SelectedExamCardType>) => {
      // update all groupId values when lens sim card is removed
      const filteredCards = prev
        .filter(
          (selectedCard) => selectedCard.examFieldType !== examAlgorithmPath
        )
        .map((selectedCard, index) => {
          return { ...selectedCard, groupId: index };
        });

      return [...filteredCards];
    });
  }, [setSelectedExamCards, examAlgorithmPath]);

  const onClickCheckbox = useCallback(() => {
    // remove lens
    if (isSelected) {
      const newLensArray = selectedLensArray.filter(
        (lens) => lens !== lensType.id
      );

      if (newLensArray.length === 0) {
        onRemoveExam();
      } else {
        updateExamCardProperty("lensIds", [...newLensArray]);
      }
    }
    // add lens
    else {
      updateExamCardProperty("lensIds", [...selectedLensArray, lensType.id]);
    }
  }, [
    lensType,
    isSelected,
    onRemoveExam,
    updateExamCardProperty,
    selectedLensArray,
  ]);

  return (
    <ButtonBase
      disabled={isDisabledButton}
      onClick={onClickCheckbox}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: "8px",
        width: "100%",
        minWidth: 0,
        padding: "6px 8px",
        borderRadius: "4px",
        border: `1px solid ${isSelected ? SELECTED_COLOR : inputBorder}`,
        backgroundColor: "transparent",
        textAlign: "left",
        transition: "border-color 0.15s ease",
        "&.Mui-disabled": { opacity: 0.5 },
      }}
    >
      {/* onClick is handled above */}
      <CustomCheckbox
        checked={isSelected}
        disabled={isDisabledButton}
        onClick={() => {}}
        checkedFill={SELECTED_COLOR}
        checkedStroke="#FFF"
        uncheckedFill="transparent"
        uncheckedStroke={inputBorder}
      />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          width: "100%",
        }}
      >
        <TitleWithTooltip
          title={lensType?.name}
          tooltip={<div style={{ whiteSpace: "pre-line" }}>{tooltip}</div>}
          titleStyle={{
            fontSize: "12px",
            color: "#FFF",
            display: "block",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textTransform: "uppercase",
            flex: 1,
            minWidth: 0,
          }}
          tooltipStyle={{
            color: "#FFF",
          }}
        />

        <Typography sx={{ fontSize: "10px", color: "#FFF" }}>
          {t("Type")}: {t(lensType?.lensType)}
        </Typography>
      </Box>
    </ButtonBase>
  );
};

export default SelectLensButton;
